/**
 * Formatadores de dados para exibição
 * 
 * Single Responsibility: Apenas formatação de dados
 * DRY: Centraliza formatações usadas em múltiplos componentes
 */

export const formatPrice = (price) => {
  if (!price) return 'A definir'
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(price)
}

export const formatDate = (dateStr) => {
  if (!dateStr) return ''
  const date = new Date(dateStr + 'T00:00:00')
  return date.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' })
}

/**
 * Data com dia da semana e ANO — sempre os dois.
 *
 * A omissão do ano foi o que escondeu o defeito de origem. A cotação da
 * Estela Fagundes (10/08/2026) nasceu com `2023-08-21` vindo do gpt-4o.
 * 21/08/2023 é segunda; 21/08/2026 é sexta. Esta função imprimia
 * "seg., 21 de ago." — e a MESMA omissão existia na view do cliente. O
 * vendedor confirmou no preview uma informação que a tela se recusava a
 * mostrar: ele não teve como flagrar.
 *
 * Uma versão intermediária mostrava o ano só quando ele divergia do
 * corrente, para não poluir o caso comum. Foi descartada por decisão de
 * produto (Diógenes, 10/08/2026): a data pública é completa, sempre.
 * Ano condicional é uma regra a mais para errar, e o dia em que ela errar
 * é exatamente o dia em que o ano estiver errado.
 *
 * Vale para as duas superfícies — preview interno e cotação pública. O que
 * NÃO atravessa para o cliente é a PROCEDÊNCIA do ano; a data em si é
 * informação da viagem e ele tem todo direito de vê-la inteira.
 */
export const formatFullDate = (dateStr) => {
  if (!dateStr) return ''
  const date = new Date(dateStr + 'T00:00:00')
  if (isNaN(date.getTime())) return ''
  return date.toLocaleDateString('pt-BR', {
    weekday: 'short', day: '2-digit', month: 'short', year: 'numeric'
  })
}

/**
 * Tipo da viagem, DERIVADO dos blocos de voo.
 *
 * Antes, `QuotacaoAereoView` imprimia "· ida + volta" fixo no resumo do
 * valor. A cotação da Estela tinha um único voo, Marabá → Salvador: o
 * cliente leu "ida + volta" numa passagem só de ida. Texto escrito à mão
 * mente na primeira vez que o dado sai do caso que o autor imaginou.
 *
 * Conexão não conta: ela vive em `details.segments` DENTRO de um bloco. O
 * que conta é quantos blocos de voo existem e se eles se espelham.
 */
export const formatTripType = (blocks = []) => {
  const voos = (blocks || []).filter(b => b && b.type === 'flight')
  if (voos.length === 0) return ''
  if (voos.length === 1) return 'somente ida'

  const pontas = voos.map(v => {
    const segs = (v.details && v.details.segments) || []
    const primeiro = segs[0] || {}
    const ultimo = segs[segs.length - 1] || primeiro
    return {
      origem: primeiro.origin || (v.details || {}).origin || '',
      destino: ultimo.destination || (v.details || {}).destination || ''
    }
  })

  const [ida, volta] = pontas
  const espelhados = voos.length === 2 &&
    ida.origem && ida.destino &&
    ida.origem === volta.destino && ida.destino === volta.origem

  return espelhados ? 'ida e volta' : `${voos.length} trechos`
}

export const formatPassengers = (adults = 1, children = 0, infants = 0) => {
  const parts = []
  // `null` não dispara o default de parâmetro (só `undefined` dispara), e a
  // API devolve null para campo não preenchido. Sem esta coerção, uma
  // cotação com `children: null` cairia silenciosamente no mesmo lugar em
  // que 2 crianças já sumiram uma vez.
  const n = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0)
  adults = n(adults); children = n(children); infants = n(infants)

  if (adults === 1) parts.push('1 adulto')
  else if (adults > 1) parts.push(`${adults} adultos`)

  if (children === 1) parts.push('1 criança')
  else if (children > 1) parts.push(`${children} crianças`)

  if (infants === 1) parts.push('1 bebê')
  else if (infants > 1) parts.push(`${infants} bebês`)

  return parts.join(', ') || '1 adulto'
}

export const getBlockIcon = (type) => {
  const icons = {
    flight: '✈️',
    hotel: '🏨',
    transfer: '🚗',
    tour: '🎯',
    car: '🚙'
  }
  return icons[type] || '📦'
}
